import { Node, Edge } from 'reactflow';
import { sendMessageToModel } from '@/services/ModelService';
import { semanticSearch } from '@/lib/semanticSearch';
import { AIModel, Message, MCPService } from '@/types';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { supabase } from '@/integrations/supabase/client';
import {
  isRuVectorAvailable,
  ruvectorClear,
  ruvectorIndexStart,
  ruvectorIndexStop,
  ruvectorSearch,
  ruvectorStats,
} from '@/lib/ruvectorClient';
import { generateEmbedding384, generateEmbedding1536 } from '@/lib/embeddings';
import { normalizeWinPath, isAgentOperationAllowed } from '@/lib/agentAccess';
import { globalVFS } from '@/OS/utils/storage/VFS';
import { mapleClawGateway, isMapleClawAvailable } from '@/lib/mapleclawGateway';
import { mapleMythosRuntime } from '@/lib/demythos/runtime';

export type SourceItem = {
  source: 'local' | 'kb';
  title: string;
  path?: string;
  snippet?: string;
  similarity?: number;
};

export type TheaterHistoryEntry = {
  nodeId: string;
  message: string;
  timestamp: string;
  role?: 'agent' | 'tool' | 'system';
  lane?: string; // 'left' | agent id (agent1..agentN)
  sources?: SourceItem[];
  /** Event kind for Cortex visualization (e.g. 'blossom', 'fork', 'deep-research-seeds'). */
  kind?: string;
  /** Blossom seeds attached to 'deep-research-seeds' events. */
  seeds?: { title: string; url: string }[];
  /** Orchestrator ID for fork/blossom events. */
  orchestratorId?: string;
  /** KB title for blossom events. */
  kbTitle?: string;
  /** KB ID for blossom events. */
  kbId?: string;
};

type AwaitingUserDecision = {
  type: 'user-decision';
  nodeId: string;
  prompt: string;
  variable: string;
  allowEmpty: boolean;
};

type AwaitingManualPause = {
  type: 'manual-pause';
  nodeId: string;
  reason?: string;
  /**
   * A hint to the UI about what "Resume" should do.
   * - blueprint: resume graph traversal from nodeId
   * - auto-evolve: resume the auto-evolve loop
   */
  resumeMode: 'blueprint' | 'auto-evolve';
};

type AwaitingState = AwaitingUserDecision | AwaitingManualPause;

// --- Agentic runtime types (restored) ---

type AgenticFlowConfig = {
  // New (multi-agent)
  agents?: {
    id: string; // stable id for lane mapping (e.g. agent1, agent2, agent3)
    name: string; // display name
    persona: string;
    modelId?: string;
  }[];

  // Back-compat (two-agent)
  agent1Persona: string;
  agent2Persona: string;

  rounds: number;
  modelSource: 'inherit' | 'custom';
  inheritedModelId?: string;
  modelA?: string;
  modelB?: string;

  retrieval: {
    useLocal: boolean;
    kbId?: string;
    topK: number;
    threshold: number;
    showSources: boolean;
    autoIndex?: boolean;
  };
  returnMode: 'last' | 'transcript' | 'summary';
};

type AgenticTranscriptItem = {
  speaker: 'user' | string; // string = agent id
  content: string;
};

type AgenticRuntimeState = {
  config: AgenticFlowConfig;
  messages: AgenticTranscriptItem[];
  lastSpeaker: string; // agent id
};

type RuntimeState = {
  visited?: string[];
  agentic?: Record<string, AgenticRuntimeState>;
};

// Define a context type to pass between nodes
export interface ExecutionContext {
  variables: Record<string, any>;
  history: TheaterHistoryEntry[];
  finalOutput?: string;
  error?: string;
  awaiting?: AwaitingState;
  lastAgenticNodeId?: string;
  runtime?: RuntimeState;
  /** Optional status hint for callers/UIs (not required for execution). */
  runStatus?: 'running' | 'paused' | 'completed' | 'failed' | 'stopped';
}

export type ExecuteBlueprintOptions = {
  /** Called whenever the execution context changes (e.g., after a new history entry is appended). */
  onContext?: (ctx: ExecutionContext) => void;
  /** Optional cooperative control hooks (pause/stop). */
  control?: {
    shouldPause?: () => boolean;
    shouldStop?: () => boolean;
    pauseMode?: AwaitingManualPause['resumeMode'];
    pauseReason?: () => string | undefined;
  };
  /** When true, disable Trust Mode prechecks/postchecks for this blueprint execution. */
  skipTrust?: boolean;
};

/**
 * Parses a cURL command string into its components for a fetch request.
 * This is a simplified parser and may not support all cURL features.
 * @param curlCommand The cURL command string or a plain URL.
 * @returns An object with url, method, headers, and body.
 */
function parseCurlCommand(curlCommand: string): { url: string; method: string; headers: Record<string, string>; body: string | undefined } {
  const trimmedCommand = curlCommand.trim();

  // If it's just a URL, handle it as a simple GET request.
  if (!trimmedCommand.startsWith('curl ')) {
    try {
      // Check if it's a valid URL.
      new URL(trimmedCommand);
      return { url: trimmedCommand, method: 'GET', headers: {}, body: undefined };
    } catch (e) {
      // Not a valid URL and not a curl command.
      return { url: '', method: 'GET', headers: {}, body: undefined };
    }
  }

  const result = {
    url: '',
    method: 'GET',
    headers: {} as Record<string, string>,
    body: undefined as string | undefined,
  };

  // Extract URL
  const urlMatch = trimmedCommand.match(/['"]?(https?:\/\/[^'"\s]+)['"]?/);
  if (urlMatch) {
    result.url = urlMatch[0].replace(/['"]/g, '');
  }

  // Extract method
  const methodMatch = trimmedCommand.match(/-X\s+([A-Z]+)|--request\s+([A-Z]+)/i);
  if (methodMatch) {
    result.method = (methodMatch[1] || methodMatch[2]).toUpperCase();
  }

  // Extract headers
  const headerRegex = /(-H|--header)\s+(['"])(.*?)\2/g;
  let match;
  while ((match = headerRegex.exec(trimmedCommand)) !== null) {
    const header = match[3];
    const separatorIndex = header.indexOf(':');
    if (separatorIndex > 0) {
      const key = header.substring(0, separatorIndex).trim();
      const value = header.substring(separatorIndex + 1).trim();
      result.headers[key] = value;
    }
  }

  // Extract data. Using `s` flag for multiline support.
  const dataRegex = /(-d|--data|--data-raw)\s+(['"])(.*?)\2/s;
  const dataMatch = trimmedCommand.match(dataRegex);
  if (dataMatch) {
    result.body = dataMatch[3];
  }

  // If method is still GET but we have a body, it should be POST
  if (result.body && result.method === 'GET') {
    result.method = 'POST';
  }

  return result;
}

function ensureRuntime(context: ExecutionContext): RuntimeState {
  if (!context.runtime) context.runtime = {};
  if (!context.runtime.agentic) context.runtime.agentic = {};
  return context.runtime;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function formatSourcesForPrompt(sources: SourceItem[]) {
  if (!sources.length) return '';
  const lines = sources.map((s, i) => {
    const sim = typeof s.similarity === 'number' ? ` (${(s.similarity * 100).toFixed(1)}%)` : '';
    const where = s.path ? ` — ${s.path}` : '';
    const snippet = s.snippet ? `\nSnippet: ${s.snippet}` : '';
    return `[${i + 1}] ${s.title}${where}${sim}${snippet}`.trim();
  });
  return lines.join('\n\n');
}

function transcriptToText(items: AgenticTranscriptItem[], agentNameById?: Record<string, string>) {
  return items
    .map((m) => {
      if (m.speaker === 'user') return `User: ${m.content}`;
      const label = agentNameById?.[m.speaker] || m.speaker;
      return `${label}: ${m.content}`;
    })
    .join('\n\n');
}

function resolveMapleMythosPromptTemplate(template: string | undefined, context: ExecutionContext) {
  const seed = template?.trim() || String(context.variables.lastOutput || context.variables.userInput || '').trim();

  return seed.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_match, key) => {
    if (key === 'input') {
      return String(context.variables.userInput || '');
    }

    if (key === 'lastOutput') {
      const value = context.variables.lastOutput;
      return typeof value === 'string' ? value : JSON.stringify(value ?? '', null, 2);
    }

    const value = context.variables[key];
    if (value === undefined || value === null) return '';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  });
}

function toPgVectorString(vec: number[]) {
  // PostgREST expects a pgvector value as a string, e.g. "[0.1,0.2,...]".
  // Passing a JS number[] often results in a 400 Bad Request.
  return `[${vec.map((n) => (Number.isFinite(n) ? n : 0)).join(',')}]`;
}

function sanitizeForPostgresText(s: string) {
  // Remove null bytes, control chars, and surrogate code points (can break JSON/PostgREST).
  return String(s)
    .replace(/\u0000/g, '')
    .replace(/[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/[\uD800-\uDFFF]/g, '')
    .trim();
}

async function retrieveSourcesForTurn(params: {
  query: string;
  retrieval: AgenticFlowConfig['retrieval'];
}): Promise<{ sources: SourceItem[]; pathUsed: 'local' | 'kb' | 'none' }>
{
  const { query, retrieval } = params;
  const topK = clamp(Number(retrieval.topK ?? 5), 1, 10);
  const threshold = clamp(Number(retrieval.threshold ?? 0.75), 0.6, 0.9);

  // Local retrieval (desktop only)
  if (retrieval.useLocal && isRuVectorAvailable()) {
    if (retrieval.autoIndex) {
      try {
        const st = await ruvectorStats();
        const osCount = (st as any)?.os?.total_vectors ?? (st as any)?.os?.totalVectors;
        if (typeof osCount === 'number' && osCount <= 0) {
          await ruvectorIndexStart(['documents']);
        }
      } catch {
        // ignore
      }
    }

    const emb = await generateEmbedding384(query);
    if (emb) {
      const res = await ruvectorSearch({ source: 'os', queryEmbedding: emb, limit: topK });
      if (res.ok) {
        const local = (res.results || [])
          .map((r: any) => ({
            source: 'local' as const,
            title: r.title || r.path || r.id,
            path: r.path,
            snippet: typeof r.snippet === 'string' ? r.snippet.slice(0, 220) : undefined,
            similarity: typeof r.similarity === 'number' ? r.similarity : undefined,
          }))
          .filter((r) => (typeof r.similarity === 'number' ? r.similarity >= threshold : true));

        if (local.length > 0) {
          return { sources: local.slice(0, topK), pathUsed: 'local' };
        }
      }
    }
  }

  // KB fallback (web or local empty)
  if (retrieval.kbId) {
    const docs = await semanticSearch(retrieval.kbId, query, { limit: topK, threshold });
    const kb = (docs || []).map((d: any) => ({
      source: 'kb' as const,
      title: d.name || d.id,
      path: d.name,
      snippet: typeof d.content === 'string' ? d.content.slice(0, 220) : undefined,
      similarity: typeof d.similarity === 'number' ? d.similarity : undefined,
    }));

    if (kb.length > 0) return { sources: kb, pathUsed: 'kb' };
  }

  return { sources: [], pathUsed: 'none' };
}

async function runAgenticTurns(params: {
  nodeId: string;
  context: ExecutionContext;
  rounds: number;
  appModels: AIModel[];
  addHistory: (msg: string, meta?: Pick<TheaterHistoryEntry, 'role' | 'lane' | 'sources'>) => void;
  control?: ExecuteBlueprintOptions['control'];
}): Promise<{ lastModelUsed?: AIModel }>
{
  const { nodeId, context, appModels, addHistory, control } = params;
  const rounds = clamp(Number(params.rounds ?? 1), 1, 60);

  const runtime = ensureRuntime(context);
  const agentic = runtime.agentic!;
  const state = agentic[nodeId];
  if (!state) {
    throw new Error('Agentic Flow runtime state missing.');
  }

  // Determine agents list (multi-agent), preserving backward compatibility
  let agents = Array.isArray(state.config.agents) ? state.config.agents : undefined;
  if (!agents || agents.length < 2) {
    agents = [
      {
        id: 'agent1',
        name: 'Agent 1',
        persona: state.config.agent1Persona,
        modelId: state.config.modelA,
      },
      {
        id: 'agent2',
        name: 'Agent 2',
        persona: state.config.agent2Persona,
        modelId: state.config.modelB,
      },
    ];
  }

  // Ensure transcript has the initial user input once.
  if (state.messages.length === 0) {
    const userInput = String(context.variables.userInput ?? '').trim();
    if (userInput) state.messages.push({ speaker: 'user', content: userInput });

    const upstream = context.variables.lastOutput;
    if (upstream && upstream !== userInput) {
      const upstreamStr = typeof upstream === 'string' ? upstream : JSON.stringify(upstream, null, 2);
      if (upstreamStr.trim().length > 0) {
        state.messages.push({ speaker: 'user', content: `Upstream context:\n${upstreamStr}` });
      }
    }
  }

  const aiConfig = context.variables.aiConfig || {};
  let lastModelUsed: AIModel | undefined;

  const idxById = new Map<string, number>(agents.map((a, i) => [a.id, i]));
  const lastIdx = idxById.get(state.lastSpeaker) ?? -1;

  for (let i = 0; i < rounds; i++) {
    if (control?.shouldStop?.()) {
      addHistory('Stopped by user.', { role: 'system', lane: 'left' });
      context.runStatus = 'stopped';
      return { lastModelUsed };
    }

    if (control?.shouldPause?.()) {
      addHistory('Paused by user.', { role: 'system', lane: 'left' });
      context.awaiting = {
        type: 'manual-pause',
        nodeId,
        reason: control?.pauseReason?.(),
        resumeMode: control?.pauseMode || 'blueprint',
      };
      context.runStatus = 'paused';
      return { lastModelUsed };
    }

    const nextIdx = (lastIdx + 1 + i + agents.length * 1000) % agents.length;
    const agent = agents[nextIdx];

    const modelId =
      state.config.modelSource === 'custom'
        ? agent.modelId
        : (state.config.inheritedModelId || context.variables.selectedModelId);

    let model = modelId ? appModels.find((m) => m.id === modelId) : undefined;
    if (!model) {
      model = appModels.find((m) => m.provider === 'openai' && m.apiName.includes('gpt')) || appModels[0];
    }

    if (!model) throw new Error('No suitable AI model available for Agentic Flow.');

    // Build query for retrieval
    const lastOther = [...state.messages]
      .reverse()
      .find((m) => m.speaker !== 'user' && m.speaker !== agent.id)?.content;

    const turnQuery = [
      `User goal: ${String(context.variables.userInput ?? '')}`,
      lastOther ? `Most recent other agent turn: ${lastOther}` : '',
      `Current state: ${String(context.variables.lastOutput ?? '')}`,
    ]
      .filter(Boolean)
      .join('\n\n');

    const { sources, pathUsed } = await retrieveSourcesForTurn({ query: turnQuery, retrieval: state.config.retrieval });

    if (pathUsed === 'none') {
      addHistory('Agentic Flow retrieval: none (running ungrounded)', { role: 'system', lane: 'left' });
    } else {
      addHistory(`Agentic Flow retrieval: ${pathUsed} • ${sources.length} hit(s)`, { role: 'system', lane: 'left' });
    }

    const baseSystemPrompt = String(context.variables.systemPrompt || '').trim();
    const sourcesBlock = sources.length ? `\n\nRelevant sources (use if helpful):\n${formatSourcesForPrompt(sources)}` : '';

    const systemContent = [
      baseSystemPrompt,
      `You are ${agent.name}.`,
      agent.persona,
      'You are participating in a multi-agent round-robin discussion. Respond with your single next turn. Be direct and avoid repeating earlier turns.',
      sourcesBlock,
    ]
      .filter(Boolean)
      .join('\n\n')
      .trim();

    const historyMessages: Partial<Message>[] = state.messages.map((m) => {
      if (m.speaker === 'user') return { role: 'user', content: m.content };
      const speakerName = agents!.find((a) => a.id === m.speaker)?.name || m.speaker;
      return { role: 'assistant', content: `[${speakerName}] ${m.content}` };
    });

    const messages: Partial<Message>[] = [{ role: 'system', content: systemContent }, ...historyMessages];

    addHistory(`Agentic Flow: ${agent.name} is responding…`, {
      role: 'system',
      lane: 'left',
    });

    const response = await sendMessageToModel(messages as Message[], model, appModels, aiConfig);

    const content = String((response as any)?.content ?? '').trim();
    state.messages.push({ speaker: agent.id, content });
    state.lastSpeaker = agent.id;

    lastModelUsed = model;

    addHistory(content || '(empty response)', {
      role: 'agent',
      lane: agent.id as any,
      sources: state.config.retrieval.showSources ? sources : undefined,
    });

    context.variables.lastOutput = content;
  }

  context.lastAgenticNodeId = nodeId;

  return { lastModelUsed };
}

async function summarizeAgentic(params: {
  context: ExecutionContext;
  nodeId: string;
  appModels: AIModel[];
  addHistory: (msg: string, meta?: Pick<TheaterHistoryEntry, 'role' | 'lane'>) => void;
  modelHint?: AIModel;
}): Promise<string>
{
  const { context, nodeId, appModels, addHistory, modelHint } = params;
  const runtime = ensureRuntime(context);
  const state = runtime.agentic?.[nodeId];

  const agents = Array.isArray(state?.config?.agents) ? state!.config.agents! : undefined;
  const agentNameById = agents ? Object.fromEntries(agents.map((a) => [a.id, a.name])) : undefined;

  const transcript = state ? transcriptToText(state.messages, agentNameById) : '';

  let model = modelHint;
  if (!model) {
    const modelId = context.variables.selectedModelId;
    model = modelId ? appModels.find((m) => m.id === modelId) : undefined;
  }
  if (!model) model = appModels[0];
  if (!model) return transcript;

  addHistory('Agentic Flow: summarizing…', { role: 'system', lane: 'left' });

  const aiConfig = context.variables.aiConfig || {};
  const messages: Partial<Message>[] = [
    {
      role: 'system',
      content:
        'Summarize the following multi-agent discussion. Provide: (1) key points per agent, (2) open questions, (3) a recommended next step. Keep it concise.',
    },
    { role: 'user', content: transcript },
  ];

  const response = await sendMessageToModel(messages as Message[], model, appModels, aiConfig);
  return String((response as any)?.content ?? '').trim();
}

async function runFromQueue(params: {
  nodes: Node[];
  edges: Edge[];
  context: ExecutionContext;
  queue: string[];
  visited: Set<string>;
  appModels: AIModel[];
  mcpServices: MCPService[];
  userApiKeys: Record<string, string>;
  onContext?: ExecuteBlueprintOptions['onContext'];
  control?: ExecuteBlueprintOptions['control'];
}): Promise<ExecutionContext> {
  const { nodes, edges, context, queue, visited, appModels, mcpServices, userApiKeys, onContext, control } = params;

  const rt = ensureRuntime(context);

  // Defensive check: Ensure nodes is an array
  if (!Array.isArray(nodes)) {
    console.error('executeBlueprint received non-array nodes:', nodes);
    throw new Error('Blueprint execution failed: Nodes data is not a valid array.');
  }

  // 1. Build adjacency list from edges for graph traversal
  const adj: Record<string, { target: string; sourceHandle?: string | null }[]> = {};
  nodes.forEach((n) => {
    adj[n.id] = [];
  });
  edges.forEach((e) => {
    if (adj[e.source]) {
      adj[e.source].push({ target: e.target, sourceHandle: e.sourceHandle });
    }
  });

  const addHistory = (nodeId: string, message: string, meta?: Pick<TheaterHistoryEntry, 'role' | 'lane' | 'sources'>) => {
    context.history.push({
      nodeId,
      message,
      timestamp: new Date().toISOString(),
      ...meta,
    });

    // Live UI hook: allow caller to observe incremental progress.
    onContext?.(context);
  };

  // 3. Process the graph node by node
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    // Persist visited continuously so pause/resume can pick up cleanly.
    rt.visited = Array.from(visited);

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) continue;

    let nextNodeIds: string[] = [];

    try {
      // 4. Execute node based on its type
      switch (node.type) {
        case 'start':
          addHistory(nodeId, 'Executing Start Node', { role: 'system', lane: 'left' });
          break;

        case 'user-input':
          // The initialInput from the dialog is already stored in context.variables.userInput.
          // This node now primarily serves as a placeholder for user interaction in the flow.
          // It does NOT overwrite lastOutput, allowing data from previous nodes to persist.
          addHistory(nodeId, `User Input node encountered. Original input: "${context.variables.userInput}"`, {
            role: 'system',
            lane: 'left',
          });
          break;

        case 'user-decision': {
          const prompt = String(node.data?.prompt || 'Continue?');
          const variable = String(node.data?.variable || 'decisionInput');
          const allowEmpty = Boolean(node.data?.allowEmpty);

          addHistory(nodeId, `Awaiting user input: ${prompt}`, { role: 'system', lane: 'left' });

          context.awaiting = {
            type: 'user-decision',
            nodeId,
            prompt,
            variable,
            allowEmpty,
          };
          context.runStatus = 'paused';

          // persist visited set for resumability
          rt.visited = Array.from(visited);

          return context;
        }

        case 'configuration':
          addHistory(nodeId, 'Processing Configuration Node', { role: 'system', lane: 'left' });
          context.variables.aiConfig = {
            ...context.variables.aiConfig,
            temperature: node.data.temperature,
            max_tokens: node.data.max_tokens,
            top_p: node.data.top_p,
          };
          addHistory(
            nodeId,
            `Set AI config: temp=${node.data.temperature ?? 'default'}, max_tokens=${node.data.max_tokens ?? 'default'}, top_p=${node.data.top_p ?? 'default'}`,
            { role: 'system', lane: 'left' }
          );
          break;

        case 'system-prompt': {
          const currentPrompt = context.variables.systemPrompt || '';
          context.variables.systemPrompt = `${currentPrompt}\n${node.data.content}`.trim();
          addHistory(nodeId, `Appended System Prompt: "${node.data.content}"`, { role: 'system', lane: 'left' });
          break;
        }

        case 'model-selector': {
          // accept multiple keys written by different UIs
          const raw =
            (node.data?.modelId ?? node.data?.model ?? node.data?.content ?? node.data?.selectedModelId ?? '') as string;

          const modelKey = (typeof raw === 'string' ? raw : String(raw)).trim();

          // If nothing was selected, treat this node as a no-op.
          // This is important for AI Blueprint Builder templates where model selection is optional.
          if (!modelKey) {
            addHistory(nodeId, 'Model Selector: no model selected (skipping; using default/inherited model).', {
              role: 'system',
              lane: 'left',
            });
            break;
          }

          // find by several common fields
          const selectedModel = modelKey
            ? appModels.find(
                (m: any) => m.id === modelKey || m.model === modelKey || m.apiName === modelKey || m.name === modelKey
              )
            : undefined;

          if (!selectedModel) {
            // Optional: fallback to first available model instead of hard error
            if (appModels.length > 0) {
              const fallback = appModels[0];
              addHistory(nodeId, `Model "${modelKey}" not found; defaulting to "${fallback.name}".`, {
                role: 'system',
                lane: 'left',
              });
              context.variables.selectedModelId = (fallback as any).id ?? fallback.name ?? (fallback as any).model;
              break;
            }
            throw new Error(`Model "${modelKey}" not found and no models are available.`);
          }

          // persist for downstream nodes
          context.variables.selectedModelId = (selectedModel as any).id ?? (selectedModel as any).model ?? selectedModel.name;

          addHistory(nodeId, `Selected Model: "${selectedModel.name}"`, { role: 'system', lane: 'left' });
          break;
        }

        case 'data': {
          const sourceType = node.data.sourceType || 'static-text';
          addHistory(nodeId, `Processing Data Node (type: ${sourceType})`, { role: 'system', lane: 'left' });

          switch (sourceType) {
            case 'static-text':
              context.variables.lastOutput = node.data.content;
              addHistory(nodeId, 'Using static text content.', { role: 'system', lane: 'left' });
              break;

            case 'file-upload': {
              const fileInfo = node.data.file;
              if (!fileInfo || !fileInfo.path) {
                throw new Error('Data node is set to file upload, but no file has been uploaded.');
              }
              addHistory(nodeId, `Downloading file: ${fileInfo.name}`, { role: 'tool', lane: 'left' });
              const { data: blob, error: downloadError } = await supabase.storage
                .from('blueprint_files')
                .download(fileInfo.path);
              if (downloadError) throw downloadError;

              let fileContent;
              if (fileInfo.name.endsWith('.csv')) {
                const text = await blob.text();
                const parsed = Papa.parse(text, { header: true });
                fileContent = JSON.stringify(parsed.data, null, 2);
              } else if (fileInfo.name.endsWith('.xlsx')) {
                const buffer = await blob.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: 'buffer' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                fileContent = JSON.stringify(json, null, 2);
              } else {
                // Assume .txt or other text format
                fileContent = await blob.text();
              }
              context.variables.lastOutput = fileContent;
              addHistory(nodeId, `Loaded content from ${fileInfo.name}.`, { role: 'tool', lane: 'left' });
              break;
            }

            case 'database':
              throw new Error('Database connection for blueprints is not yet implemented.');

            default:
              context.variables.lastOutput = node.data.content;
              addHistory(nodeId, 'Using static text content (default).', { role: 'system', lane: 'left' });
              break;
          }
          break;
        }

        case 'knowledge-base': {
          const mode = String(node.data.mode || 'select');

          if (mode === 'deep-research') {
            addHistory(nodeId, 'Deep Research: starting web research…', { role: 'tool', lane: 'left' });

            // Resolve search queries
            const cfg = (node.data.deepResearch || {}) as any;
            const rawQueries = String(cfg.queries || '').trim();
            const queries = rawQueries
              ? rawQueries
                  .split('\n')
                  .map((s: string) => s.trim())
                  .filter(Boolean)
              : [String(context.variables.userInput || context.variables.lastOutput || '').trim()].filter(Boolean);

            if (!queries.length) {
              throw new Error('Deep Research is enabled but no search terms were provided.');
            }

            const resultsPerQuery = clamp(Number(cfg.resultsPerQuery ?? 6), 1, 10);
            const scrapeTopN = clamp(Number(cfg.scrapeTopN ?? 4), 0, 10);

            // Create a new KB for this research run
            const kbName = String(cfg.kbName || `Deep Research — ${new Date().toLocaleString()}`).slice(0, 120);
            const kbDescription = String(cfg.kbDescription || `Auto-created by Deep Research from: ${queries[0]}`).slice(0, 500);

            const { data: userRes } = await supabase.auth.getUser();
            const uid = userRes?.user?.id;
            if (!uid) throw new Error('Deep Research requires an authenticated user.');

            const { data: kbRow, error: kbErr } = await supabase
              .from('knowledge_bases')
              .insert({
                name: kbName,
                description: kbDescription,
                organization_id: null,
                created_by: uid,
                is_private: true,
              })
              .select('id, name')
              .single();

            if (kbErr) throw kbErr;

            const kbId = String(kbRow.id);
            addHistory(nodeId, `Deep Research: created knowledge base: "${kbRow.name}"`, {
              role: 'tool',
              lane: 'left',
            });

            const sourceItems: SourceItem[] = [];
            const insertedDocs: { name: string; url: string }[] = [];

            const insertKbDoc = async (payload: any) => {
              // Always request representation to get a better error payload from PostgREST.
              return await supabase
                .from('knowledge_base_documents')
                .insert(payload, { returning: 'minimal' } as any);
            };

            // Track sources we scraped so downstream automation can "blossom" from them.
            // Collect seeds from search results (not just successful DB inserts) so
            // blossoms appear even when embedding/insert issues prevent KB storage.
            const blossomSeeds: { title: string; url: string }[] = [];
            const seenSeedUrls = new Set<string>();

            for (const q of queries.slice(0, 8)) {
              addHistory(nodeId, `Deep Research: web search → "${q}"`, { role: 'tool', lane: 'left' });

              const { data: ws, error: wsErr } = await supabase.functions.invoke('web-search', {
                body: { query: q },
              });
              if (wsErr) throw new Error(`Web search failed: ${wsErr.message}`);

              const results = (ws as any)?.results || ws;
              const organic =
                (results && (results.organic_results || results.organicResults || results.results || results.items)) || [];

              const links: { title: string; url: string; snippet?: string }[] = [];
              if (Array.isArray(organic)) {
                for (const r of organic) {
                  const url = String(r?.link || r?.url || r?.href || '').trim();
                  if (!url) continue;
                  links.push({
                    title: String(r?.title || url),
                    url,
                    snippet: String(r?.snippet || r?.content || ''),
                  });
                  if (links.length >= resultsPerQuery) break;
                }
              }

              // Collect blossom seeds from search results immediately — these represent
              // discovered research topics regardless of whether DB inserts succeed.
              for (const l of links.slice(0, scrapeTopN)) {
                if (!seenSeedUrls.has(l.url)) {
                  seenSeedUrls.add(l.url);
                  blossomSeeds.push({ title: l.title, url: l.url });
                }
              }

              // Scrape + store the top N pages into the new KB
              for (const l of links.slice(0, scrapeTopN)) {
                addHistory(nodeId, `Deep Research: scrape → ${l.url}`, { role: 'tool', lane: 'left' });

                const { data: page, error: scrapeErr } = await supabase.functions.invoke('scrape-url', {
                  body: { url: l.url },
                });
                if (scrapeErr) {
                  addHistory(nodeId, `Deep Research: scrape failed (skipping): ${l.url}`, { role: 'system', lane: 'left' });
                  continue;
                }

                const rawText = String((page as any)?.content || (page as any)?.results?.content || '').trim();
                if (!rawText) continue;

                const sanitizedText = sanitizeForPostgresText(rawText);
                if (!sanitizedText) continue;

                const docName = sanitizeForPostgresText(String(l.title || l.url)).slice(0, 180);

                // Keep documents reasonably sized to avoid PostgREST payload limits.
                const MAX_DOC_CHARS = 12_000;
                const docContentRaw = `# ${docName}\n\nSource: ${l.url}\n\n${sanitizedText}`;
                const docContent = docContentRaw.length > MAX_DOC_CHARS
                  ? `${docContentRaw.slice(0, MAX_DOC_CHARS)}\n\n…(truncated)`
                  : docContentRaw;

                let embedding1536: number[] | null = null;
                let embedding384: number[] | null = null;

                // NOTE: This project has seen both 1536-d and 384-d KB embedding schemas in the wild.
                // We detect the dimension expected by the DB on the fly and cache it in context.variables.kbEmbeddingDim.
                const preferredDim = Number(context.variables.kbEmbeddingDim || 0) || 0; // 0 = unknown

                const baseInsertPayload: any = {
                  knowledge_base_id: kbId,
                  name: docName,
                  content: docContent,
                  file_type: 'markdown',
                  size: docContent.length,
                  storage_path: null,
                };

                const tryInsertWithEmbedding = async (vec: number[]) => {
                  const embStr = toPgVectorString(vec);
                  const res = await insertKbDoc({
                    ...baseInsertPayload,
                    embedding: embStr as any,
                  });
                  return { ...res, embStr };
                };

                const detectExpectedDim = (err: any): 0 | 384 | 1536 => {
                  const msg = String(err?.message || '').toLowerCase();
                  if (msg.includes('expected 384')) return 384;
                  if (msg.includes('expected 1536')) return 1536;
                  // Common phrasing: "expected 384 dimensions, not 1536"
                  if (msg.includes('dimensions') && msg.includes('not 1536') && msg.includes('384')) return 384;
                  if (msg.includes('dimensions') && msg.includes('not 384') && msg.includes('1536')) return 1536;
                  return 0;
                };

                // Attempt insert with embedding first; if that fails, retry without embedding.
                let insertError: any = null;

                // Choose which embedding to try first.
                // Default to 384 first because it matches RuVector/local search and avoids noisy 400s
                // in environments where the KB embedding column is still 384-d.
                const tryFirst: 384 | 1536 | 0 =
                  preferredDim === 384 || preferredDim === 1536 ? (preferredDim as any) : 384;

                if (tryFirst === 1536) {
                  try {
                    embedding1536 = await generateEmbedding1536(docContent.slice(0, 8000));
                  } catch {
                    embedding1536 = null;
                  }

                  if (embedding1536 && embedding1536.length === 1536) {
                    const { error: withEmbErr, embStr } = await tryInsertWithEmbedding(embedding1536);
                    insertError = withEmbErr;

                    if (withEmbErr) {
                      const expected = detectExpectedDim(withEmbErr);
                      if (expected) context.variables.kbEmbeddingDim = expected;

                      console.error(
                        `[Deep Research] insert with embedding failed: ${String(withEmbErr.message || withEmbErr)} ` +
                          `(code=${String((withEmbErr as any).code || 'n/a')}, expectedDim=${expected || 'unknown'}, triedDim=1536)`
                      );
                      console.error('[Deep Research] insert with embedding failed details', {
                        message: withEmbErr.message,
                        details: (withEmbErr as any).details,
                        hint: (withEmbErr as any).hint,
                        code: (withEmbErr as any).code,
                        kbId,
                        name: docName,
                        contentLen: docContent.length,
                        embeddingLen: embedding1536.length,
                        embeddingPreview: embStr.slice(0, 48) + '…',
                        detectedExpectedDim: expected || null,
                      });

                      // If the DB expects 384 dimensions, fall back and retry with 384.
                      if (expected === 384) {
                        console.warn('[Deep Research] DB expects 384 dims; retrying with 384 embedding…', { kbId, name: docName });
                        try {
                          embedding384 = await generateEmbedding384(docContent.slice(0, 8000));
                        } catch {
                          embedding384 = null;
                        }

                        if (embedding384 && embedding384.length === 384) {
                          const { error: retry384Err } = await tryInsertWithEmbedding(embedding384);
                          insertError = retry384Err;

                          if (retry384Err) {
                            console.error(
                              `[Deep Research] retry insert with 384-d embedding failed: ${String(
                                retry384Err.message || retry384Err
                              )} (code=${String((retry384Err as any).code || 'n/a')})`
                            );
                            console.error('[Deep Research] retry insert with 384-d embedding failed details', {
                              message: retry384Err.message,
                              details: (retry384Err as any).details,
                              hint: (retry384Err as any).hint,
                              code: (retry384Err as any).code,
                              kbId,
                              name: docName,
                              contentLen: docContent.length,
                              embeddingLen: embedding384.length,
                            });
                          } else {
                            console.log('[Deep Research] retry with 384 embedding succeeded', { kbId, name: docName });
                            insertedDocs.push({ name: docName, url: l.url });
                            sourceItems.push({ source: 'kb', title: docName, path: l.url, snippet: l.snippet?.slice(0, 220) });
                            continue;

                          }
                        } else {
                          console.warn('[Deep Research] 384-d embedding generation unavailable; falling back to no-embedding insert.', {
                            kbId,
                            name: docName,
                          });
                        }
                      }

                      addHistory(nodeId, `Deep Research: embedding insert failed, retrying without embedding: ${docName}`, {
                        role: 'system',
                        lane: 'left',
                      });
                    }
                  }
                } else if (tryFirst === 384) {
                  try {
                    embedding384 = await generateEmbedding384(docContent.slice(0, 8000));
                  } catch {
                    embedding384 = null;
                  }

                  if (embedding384 && embedding384.length === 384) {
                    const { error: withEmbErr, embStr } = await tryInsertWithEmbedding(embedding384);
                    insertError = withEmbErr;

                    if (withEmbErr) {
                      const expected = detectExpectedDim(withEmbErr);
                      if (expected) context.variables.kbEmbeddingDim = expected;

                      console.error(
                        `[Deep Research] insert with embedding failed: ${String(withEmbErr.message || withEmbErr)} ` +
                          `(code=${String((withEmbErr as any).code || 'n/a')}, expectedDim=${expected || 'unknown'}, triedDim=384)`
                      );
                      console.error('[Deep Research] insert with embedding failed details', {
                        message: withEmbErr.message,
                        details: (withEmbErr as any).details,
                        hint: (withEmbErr as any).hint,
                        code: (withEmbErr as any).code,
                        kbId,
                        name: docName,
                        contentLen: docContent.length,
                        embeddingLen: embedding384.length,
                        embeddingPreview: embStr.slice(0, 48) + '…',
                        detectedExpectedDim: expected || null,
                      });

                      // If the DB expects 1536 dimensions, fall back and retry with 1536.
                      if (expected === 1536) {
                        console.warn('[Deep Research] DB expects 1536 dims; retrying with 1536 embedding…', { kbId, name: docName });
                        try {
                          embedding1536 = await generateEmbedding1536(docContent.slice(0, 8000));
                        } catch {
                          embedding1536 = null;
                        }

                        if (embedding1536 && embedding1536.length === 1536) {
                          const { error: retry1536Err } = await tryInsertWithEmbedding(embedding1536);
                          insertError = retry1536Err;

                          if (retry1536Err) {
                            console.error(
                              `[Deep Research] retry insert with 1536-d embedding failed: ${String(
                                retry1536Err.message || retry1536Err
                              )} (code=${String((retry1536Err as any).code || 'n/a')})`
                            );
                          } else {
                            console.log('[Deep Research] retry with 1536 embedding succeeded', { kbId, name: docName });
                            insertedDocs.push({ name: docName, url: l.url });
                            sourceItems.push({ source: 'kb', title: docName, path: l.url, snippet: l.snippet?.slice(0, 220) });
                            continue;

                          }
                        }
                      }

                      addHistory(nodeId, `Deep Research: embedding insert failed, retrying without embedding: ${docName}`, {
                        role: 'system',
                        lane: 'left',
                      });
                    }
                  }
                }

                if (!insertError) {
                  // Insert succeeded with embedding OR embedding wasn't attempted
                  insertedDocs.push({ name: docName, url: l.url });
                  sourceItems.push({ source: 'kb', title: docName, path: l.url, snippet: l.snippet?.slice(0, 220) });
                  continue;
                }

                // Retry without embedding
                const { error: docErr } = await insertKbDoc(baseInsertPayload);

                if (!docErr) {
                  insertedDocs.push({ name: docName, url: l.url });
                  sourceItems.push({ source: 'kb', title: docName, path: l.url, snippet: l.snippet?.slice(0, 220) });
                } else {

                  console.error(
                    `[Deep Research] insert without embedding failed: ${String(docErr.message || docErr)} ` +
                      `(code=${String((docErr as any).code || 'n/a')})`
                  );
                  console.error('[Deep Research] insert without embedding failed details', {
                    message: docErr.message,
                    details: (docErr as any).details,
                    hint: (docErr as any).hint,
                    code: (docErr as any).code,
                    kbId,
                    name: docName,
                    contentLen: docContent.length,
                  });

                  // Final fallback: try a minimal tiny row. If THIS fails too, it's likely RLS/relationship/schema.
                  const minimalPayload = {
                    knowledge_base_id: kbId,
                    name: `${docName}`.slice(0, 180),
                    content: `Source: ${l.url}`,
                    file_type: 'markdown',
                    size: `Source: ${l.url}`.length,
                    storage_path: null,
                  };
                  const { error: minErr } = await insertKbDoc(minimalPayload);

                  if (minErr) {
                    console.error(
                      `[Deep Research] minimal insert failed: ${String(minErr.message || minErr)} ` +
                        `(code=${String((minErr as any).code || 'n/a')})`
                    );
                    console.error('[Deep Research] minimal insert failed details', {
                      message: minErr.message,
                      details: (minErr as any).details,
                      hint: (minErr as any).hint,
                      code: (minErr as any).code,
                      kbId,
                      name: docName,
                    });
                  }

                  const detail = (docErr as any)?.details || (docErr as any)?.hint || '';
                  const minDetail = (minErr as any)?.details || (minErr as any)?.hint || '';

                  addHistory(
                    nodeId,
                    [
                      `Deep Research: failed to store document (skipping): ${docName}`,
                      `error: ${(docErr as any)?.message || String(docErr)}${detail ? ` (${detail})` : ''}`,
                      `minimal-insert: ${minErr ? `FAILED: ${(minErr as any)?.message || String(minErr)}${minDetail ? ` (${minDetail})` : ''}` : 'OK'}`,
                      `debug: kbId=${kbId} contentLen=${docContent.length} kbEmbeddingDim=${context.variables.kbEmbeddingDim || 'unknown'}`,
                    ].join(' | '),
                    { role: 'system', lane: 'left' }
                  );
                }
              }
            }

            const summaryLines = insertedDocs.slice(0, 30).map((d, i) => `${i + 1}. ${d.name} — ${d.url}`);
            const kbContext = `Deep Research created KB: ${kbName}\nKB ID: ${kbId}\n\nCollected sources:\n${summaryLines.join('\n')}`;

            context.variables.knowledgeBaseResult = kbContext;
            context.variables.lastOutput = kbContext;
            context.variables.deepResearchKbId = kbId;

            // Expose blossoms seeds for downstream automation.
            context.variables.deepResearchSeeds = blossomSeeds;

            // Persist seeds inside the history so they survive DB round-trips
            // (context.variables is ephemeral and not saved in the run record).
            if (blossomSeeds.length) {
              context.history.push({
                nodeId,
                message: `Deep Research: ${blossomSeeds.length} blossom seeds available.`,
                timestamp: new Date().toISOString(),
                role: 'system',
                lane: 'left',
                kind: 'deep-research-seeds',
                seeds: blossomSeeds,
              });
              onContext?.(context);
            }

            addHistory(nodeId, `Deep Research: added ${insertedDocs.length} documents to KB.`, {
              role: 'tool',
              lane: 'left',
              sources: sourceItems,
            });

            break;
          }

          // Default: selected KB retrieval
          const kbId = node.data.content || context.variables.deepResearchKbId;
          if (!kbId) throw new Error('Knowledge Base node is missing a KB selection.');

          const query = context.variables.userInput; // Use the original user input as the query
          addHistory(nodeId, `Searching Knowledge Base for: "${query}"`, { role: 'tool', lane: 'left' });

          const relevantDocs = await semanticSearch(kbId, query, { limit: 5, threshold: 0.1 });
          const kbContext = relevantDocs.map((doc, i) => `Document ${i + 1}: ${doc.content}`).join('\n\n');

          context.variables.knowledgeBaseResult = kbContext;
          context.variables.lastOutput = kbContext; // Pass the found context to the next node
          addHistory(nodeId, `Found ${relevantDocs.length} documents.`, { role: 'tool', lane: 'left' });
          break;
        }

        case 'mcp': {
          const serviceId = node.data.content;
          if (!serviceId) {
            // Treat unconfigured MCP nodes as a no-op so they don't hard-fail the run.
            addHistory(nodeId, 'MCP skipped: no service selected.', { role: 'system', lane: 'left' });
            // Keep lastOutput unchanged.
            break;
          }

          const service = mcpServices.find((s) => s.id === serviceId);
          if (!service) throw new Error(`MCP Service with ID "${serviceId}" not found.`);

          const apiKey = userApiKeys[serviceId];
          if (!apiKey)
            throw new Error(`API Key for MCP Service "${service.name}" is not set. Please add it in your settings.`);

          addHistory(nodeId, `Executing MCP Service: "${service.name}"`, { role: 'tool', lane: 'left' });

          try {
            const response = await fetch(service.url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${apiKey}`, // Standard way to send a key
              },
              body: JSON.stringify({
                input: context.variables.lastOutput, // Pass previous output as input
              }),
            });

            if (!response.ok) {
              throw new Error(`MCP service call failed with status ${response.status}: ${response.statusText}`);
            }

            let serviceData;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              serviceData = await response.json();
            } else {
              serviceData = await response.text();
            }

            context.variables.mcpResult = serviceData;
            context.variables.lastOutput = typeof serviceData === 'object' ? JSON.stringify(serviceData, null, 2) : serviceData;
            addHistory(nodeId, `Received MCP Response: ${String(context.variables.lastOutput).substring(0, 100)}...`, {
              role: 'tool',
              lane: 'left',
            });
          } catch (fetchError: any) {
            throw new Error(`Failed to execute MCP service: ${fetchError.message}`);
          }
          break;
        }

        case 'mapleclaw-gateway': {
          const mode = String(node.data?.mode || 'send-message');
          const sessionKey = String(node.data?.sessionKey || 'main').trim() || 'main';
          const applyTemplate = (value: any) =>
            typeof value === 'string'
              ? value
                  .replace(/\{\{lastOutput\}\}/g, String(context.variables.lastOutput ?? ''))
                  .replace(/\{\{userInput\}\}/g, String(context.variables.userInput ?? ''))
              : value;

          if (!isMapleClawAvailable()) {
            addHistory(nodeId, 'MapleClaw Gateway skipped: unavailable in the current runtime.', { role: 'system', lane: 'left' });
            break;
          }

          if (mode === 'list-sessions') {
            addHistory(nodeId, 'MapleClaw Gateway: listing sessions…', { role: 'tool', lane: 'left' });
            const sessions = await mapleClawGateway.listSessions();
            context.variables.mapleClawSessions = sessions;
            context.variables.lastOutput = JSON.stringify(sessions, null, 2);
            addHistory(nodeId, `MapleClaw Gateway: ${sessions.length} session(s) available.`, { role: 'tool', lane: 'left' });
            break;
          }

          if (mode === 'get-transcript') {
            const tailLines = Math.max(1, Math.min(200, Number(node.data?.tailLines ?? 50)));
            addHistory(nodeId, `MapleClaw Gateway: reading transcript for "${sessionKey}"…`, { role: 'tool', lane: 'left' });
            const transcript = await mapleClawGateway.getTranscript(sessionKey, tailLines);
            context.variables.mapleClawTranscript = transcript;
            context.variables.lastOutput = JSON.stringify(transcript, null, 2);
            addHistory(nodeId, `MapleClaw Gateway: loaded ${transcript.length} transcript event(s).`, { role: 'tool', lane: 'left' });
            break;
          }

          if (mode === 'heartbeat-ping') {
            addHistory(nodeId, `MapleClaw Gateway: heartbeat for "${sessionKey}"…`, { role: 'tool', lane: 'left' });
            const result = await mapleClawGateway.enqueue({
              sessionKey,
              type: 'heartbeat',
              payload: applyTemplate(String(node.data?.payload || 'Manual MapleClaw heartbeat')),
              awaitResponse: true,
              timeoutMs: Number(node.data?.timeoutMs ?? 30000),
            });

            if (!result.ok) throw new Error(result.error || 'MapleClaw heartbeat failed.');
            context.variables.mapleClawResult = result;
            context.variables.lastOutput = String(result.content || 'HEARTBEAT_OK');
            addHistory(nodeId, `MapleClaw Gateway: ${context.variables.lastOutput}`, { role: 'tool', lane: 'left' });
            break;
          }

          const payload = applyTemplate(String(node.data?.payload || context.variables.lastOutput || context.variables.userInput || '')).trim();
          if (!payload) {
            throw new Error('MapleClaw Gateway message is empty.');
          }

          addHistory(nodeId, `MapleClaw Gateway: queueing message for "${sessionKey}"…`, { role: 'tool', lane: 'left' });
          const result = await mapleClawGateway.enqueue({
            sessionKey,
            type: 'user_message',
            payload,
            awaitResponse: Boolean(node.data?.awaitResponse ?? true),
            timeoutMs: Number(node.data?.timeoutMs ?? 30000),
            processingRuntime: String(node.data?.processingRuntime || 'local') === 'pi-agent' ? 'pi-agent' : 'local',
            modelId: String(node.data?.modelId || context.variables.selectedModelId || ''),
            agentInstruction: String(node.data?.agentInstruction || ''),
          });

          if (!result.ok) throw new Error(result.error || 'MapleClaw request failed.');

          context.variables.mapleClawResult = result;
          context.variables.lastOutput = String(result.content || 'Queued');
          addHistory(nodeId, `MapleClaw Gateway response: ${String(context.variables.lastOutput).substring(0, 140)}${String(context.variables.lastOutput).length > 140 ? '…' : ''}`, {
            role: 'tool',
            lane: 'left',
          });
          break;
        }

        case 'api-call': {
          // Supports either a raw cURL/URL in node.data.content OR a structured request object in node.data.apiRequest.
          // Also supports templating {{lastOutput}} in url/body/headers when using apiRequest.

          const inputData = context.variables.lastOutput;

          const applyTemplate = (s: any) => {
            if (typeof s !== 'string') return s;
            return s.replace(/\{\{lastOutput\}\}/g, String(inputData ?? ''));
          };

          const apiReq = node.data.apiRequest;

          let url = '';
          let method = 'GET';
          let headers: Record<string, string> = {};
          let body: string | undefined = undefined;

          if (apiReq && typeof apiReq === 'object' && (apiReq.url || node.data.url)) {
            url = applyTemplate(String(apiReq.url ?? node.data.url ?? ''));
            method = String(apiReq.method ?? node.data.method ?? 'GET').toUpperCase();

            const rawHeaders = apiReq.headers ?? node.data.headers ?? {};
            if (rawHeaders && typeof rawHeaders === 'object') {
              headers = Object.fromEntries(Object.entries(rawHeaders).map(([k, v]) => [k, applyTemplate(String(v))]));
            }

            const rawBody = apiReq.body ?? node.data.body;
            if (rawBody !== undefined && rawBody !== null && String(rawBody).length > 0) {
              body = applyTemplate(String(rawBody));
            }

            addHistory(nodeId, `Making API call to: "${url}" with method ${method}`, { role: 'tool', lane: 'left' });
          } else {
            const curlInput = node.data.content;
            if (!curlInput) throw new Error('API Call node is missing a cURL command or URL.');

            const parsed = parseCurlCommand(curlInput);
            url = parsed.url;
            method = parsed.method;
            headers = parsed.headers;
            body = parsed.body;

            if (!url) {
              throw new Error('Could not parse a valid URL from the API Call node content.');
            }

            addHistory(nodeId, `Making API call to: "${url}" with method ${method}`, { role: 'tool', lane: 'left' });
          }

          try {
            const response = await fetch(url, {
              method,
              headers,
              body,
            });
            if (!response.ok) {
              throw new Error(`API call failed with status ${response.status}: ${response.statusText}`);
            }
            let apiData;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              try {
                apiData = await response.json();
              } catch (jsonError) {
                addHistory(nodeId, 'Warning: API response said it was JSON but failed to parse. Treating as text.', {
                  role: 'system',
                  lane: 'left',
                });
                apiData = await response.text();
              }
            } else {
              apiData = await response.text();
            }
            context.variables.apiResult = apiData;
            context.variables.lastOutput = typeof apiData === 'object' ? JSON.stringify(apiData, null, 2) : apiData;
            addHistory(nodeId, `Received API Response: ${String(context.variables.lastOutput).substring(0, 100)}...`, {
              role: 'tool',
              lane: 'left',
            });
          } catch (fetchError: any) {
            throw new Error(`Failed to fetch from API: ${fetchError.message}`);
          }
          break;
        }

        case 'action': {
          const actionType = node.data.actionType || 'json-extract';
          const inputData = context.variables.lastOutput;
          addHistory(nodeId, `Executing Action Node (type: ${actionType})`, { role: 'tool', lane: 'left' });

          try {
            switch (actionType) {
              case 'json-extract': {
                const path = node.data.jsonPath;
                if (!path) throw new Error('JSON Path is not defined for Extract JSON Property action.');

                let jsonData;
                try {
                  jsonData = typeof inputData === 'string' ? JSON.parse(inputData) : inputData;
                } catch (e) {
                  throw new Error('Input data is not valid JSON for extraction.');
                }

                const result = path.split('.').reduce((o: any, k: string) => {
                  const arrMatch = k.match(/(\w+)\[(\d+)\]/);
                  if (arrMatch) {
                    const key = arrMatch[1];
                    const index = parseInt(arrMatch[2], 10);
                    return o && o[key] && Array.isArray(o[key]) && o[key][index] !== undefined ? o[key][index] : undefined;
                  }
                  return o && o[k] !== undefined ? o[k] : undefined;
                }, jsonData);

                if (result === undefined) {
                  addHistory(nodeId, `Warning: Path "${path}" not found in JSON object. Output is undefined.`, {
                    role: 'system',
                    lane: 'left',
                  });
                }
                context.variables.lastOutput = typeof result === 'object' ? JSON.stringify(result, null, 2) : result;
                addHistory(nodeId, `Extracted value from path "${path}".`, { role: 'tool', lane: 'left' });
                break;
              }

              case 'text-template': {
                const template = node.data.template;
                if (!template) throw new Error('Template is not defined for Text Templating action.');

                const templatedOutput = template.replace(/\{\{lastOutput\}\}/g, String(inputData));
                context.variables.lastOutput = templatedOutput;
                addHistory(nodeId, 'Applied template.', { role: 'tool', lane: 'left' });
                break;
              }

              case 'text-split': {
                const delimiter = node.data.delimiter === '\\n' ? '\n' : node.data.delimiter || ',';
                if (typeof inputData !== 'string') throw new Error('Input for Split Text must be a string.');

                const splitResult = inputData.split(delimiter);
                context.variables.lastOutput = splitResult;
                addHistory(nodeId, `Split text by "${node.data.delimiter}". Result is an array of ${splitResult.length} items.`, {
                  role: 'tool',
                  lane: 'left',
                });
                break;
              }

              case 'list-join': {
                const delimiter = node.data.delimiter === '\\n' ? '\n' : node.data.delimiter || ', ';
                if (!Array.isArray(inputData)) throw new Error('Input for Join List must be an array.');

                const joinResult = inputData.join(delimiter);
                context.variables.lastOutput = joinResult;
                addHistory(nodeId, `Joined list with "${node.data.delimiter}".`, { role: 'tool', lane: 'left' });
                break;
              }

              default:
                addHistory(nodeId, `Unknown action type: ${actionType}. Passing data through.`, { role: 'system', lane: 'left' });
                context.variables.lastOutput = inputData;
                break;
            }
          } catch (e: any) {
            throw new Error(`Action failed: ${e.message}`);
          }
          break;
        }

        case 'conditional': {
          addHistory(nodeId, 'Executing Conditional Node', { role: 'system', lane: 'left' });
          const { operator = 'contains', value: conditionValue } = node.data;
          const sourceValue = context.variables.lastOutput;
          let result = false;

          const numSource = parseFloat(sourceValue);
          const numCondition = parseFloat(conditionValue);

          switch (operator) {
            case 'contains':
              result = String(sourceValue).includes(conditionValue);
              break;
            case 'not-contains':
              result = !String(sourceValue).includes(conditionValue);
              break;
            case '==':
              // eslint-disable-next-line eqeqeq
              result = sourceValue == conditionValue;
              break;
            case '!=':
              // eslint-disable-next-line eqeqeq
              result = sourceValue != conditionValue;
              break;
            case '>':
              result = !isNaN(numSource) && !isNaN(numCondition) && numSource > numCondition;
              break;
            case '<':
              result = !isNaN(numSource) && !isNaN(numCondition) && numSource < numCondition;
              break;
            case '>=':
              result = !isNaN(numSource) && !isNaN(numCondition) && numSource >= numCondition;
              break;
            case '<=':
              result = !isNaN(numSource) && !isNaN(numCondition) && numSource <= numCondition;
              break;
            default:
              addHistory(nodeId, `Unknown operator: ${operator}`, { role: 'system', lane: 'left' });
          }

          addHistory(nodeId, `Condition evaluated: lastOutput (${sourceValue}) ${operator} ${conditionValue} -> ${result}`, {
            role: 'system',
            lane: 'left',
          });
          const handleId = result ? 'true' : 'false';
          const outgoingEdges = adj[nodeId] || [];
          const nextEdge = outgoingEdges.find((edge) => edge.sourceHandle === handleId);

          if (nextEdge) {
            nextNodeIds = [nextEdge.target];
            addHistory(nodeId, `Following '${handleId}' path.`, { role: 'system', lane: 'left' });
          } else {
            addHistory(nodeId, `No path found for '${handleId}'. Stopping this branch.`, { role: 'system', lane: 'left' });
          }
          break;
        }

        case 'intelligencexp': {
          const mode = String(node.data?.mode || 'search');

          const topK = clamp(Number(node.data?.topK ?? 5), 1, 10);
          const threshold = clamp(Number(node.data?.threshold ?? 0.75), 0.6, 0.9);

          if (mode === 'search') {
            const domain = String(node.data?.domain || 'files'); // 'files' | 'knowledge-base'
            const querySource = String(node.data?.querySource || 'user-input');

            const resolveQuery = () => {
              if (querySource === 'custom') return String(node.data?.customQuery || '');
              if (querySource === 'last-output') return String(context.variables.lastOutput ?? '');
              return String(context.variables.userInput ?? '');
            };

            const q = resolveQuery().trim();
            if (!q) {
              throw new Error('IntelligenceXP Search: query is empty.');
            }

            const kbId = String(node.data?.kbId || '').trim();
            const shouldUseLocal = domain === 'files' && isRuVectorAvailable();

            let results: { path?: string; title: string; snippet?: string; similarity?: number }[] = [];

            if (shouldUseLocal) {
              addHistory(nodeId, `IntelligenceXP(Search): local files • topK=${topK}`, { role: 'tool', lane: 'left' });

              const emb = await generateEmbedding384(q);
              if (!emb) {
                throw new Error('IntelligenceXP Search: failed to generate local (384-d) embedding.');
              }

              const res = await ruvectorSearch({ source: 'os', queryEmbedding: emb, limit: topK });
              if (!res.ok) {
                throw new Error(res.error || 'IntelligenceXP Search: local search failed.');
              }

              results = (res.results || []).map((r: any) => ({
                path: r.path,
                title: r.title || r.path || r.id,
                snippet: r.snippet,
                similarity: r.similarity,
              }));

              addHistory(nodeId, `IntelligenceXP(Search): ${results.length} results (local)`, { role: 'tool', lane: 'left' });
            } else {
              if (!kbId) {
                addHistory(
                  nodeId,
                  domain === 'files'
                    ? 'IntelligenceXP(Search): local search unavailable on web and no KB configured for fallback. Skipping.'
                    : 'IntelligenceXP(Search): Knowledge Base not selected. Skipping.',
                  { role: 'system', lane: 'left' }
                );
                context.variables.intelligenceXPResults = [];
                context.variables.lastOutput = JSON.stringify([], null, 2);
                break;
              }

              const fallbackNote = domain === 'files' && !isRuVectorAvailable();
              addHistory(nodeId, `IntelligenceXP(Search): knowledge base • topK=${topK}${fallbackNote ? ' (web fallback from Files)' : ''}`, {
                role: 'tool',
                lane: 'left',
              });

              const docs = await semanticSearch(kbId, q, { limit: topK, threshold });
              results = (docs || []).map((d: any) => ({
                path: d.name,
                title: d.name || d.id,
                snippet: typeof d.content === 'string' ? d.content.slice(0, 280) : undefined,
                similarity: d.similarity,
              }));

              addHistory(
                nodeId,
                `IntelligenceXP(Search): ${results.length} results (KB)` + (fallbackNote ? ' • fallback: KB search' : ''),
                { role: 'tool', lane: 'left' }
              );
            }

            context.variables.intelligenceXPResults = results;
            context.variables.lastOutput = JSON.stringify(results, null, 2);
            break;
          }

          if (mode === 'read') {
            const pathSource = String(node.data?.pathSource || 'last-output');

            const resolvePath = () => {
              if (pathSource === 'manual') return String(node.data?.manualPath || '');

              const raw = context.variables.lastOutput;

              if (typeof raw === 'string') {
                const trimmed = raw.trim();
                // If lastOutput is JSON from IntelligenceXP search
                if (
                  (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
                  (trimmed.startsWith('{') && trimmed.endsWith('}'))
                ) {
                  try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === 'object') {
                      return String((parsed[0] as any).path || '');
                    }
                    if (parsed && typeof parsed === 'object') {
                      return String((parsed as any).path || '');
                    }
                  } catch {
                    // fall through
                  }
                }

                return trimmed;
              }

              if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
                return String((raw[0] as any).path || '');
              }

              if (raw && typeof raw === 'object') {
                return String((raw as any).path || '');
              }

              return '';
            };

            const path = normalizeWinPath(resolvePath().trim());
            if (!path) {
              throw new Error('IntelligenceXP(Read): no path resolved from configuration / lastOutput.');
            }

            if (!isAgentOperationAllowed(path)) {
              addHistory(nodeId, `IntelligenceXP(Read): blocked by agent allowlist • ${path}`, { role: 'system', lane: 'left' });
              throw new Error('IntelligenceXP(Read): blocked by agent allowlist.');
            }

            addHistory(nodeId, `IntelligenceXP(Read): ${path}`, { role: 'tool', lane: 'left' });

            const api = (window as any).electronAPI;
            let content: string | null = null;

            if (api?.isElectron && typeof api.readFile === 'function') {
              content = await api.readFile(path);
            } else {
              content = await globalVFS.load(path);
            }

            if (content === null || content === undefined) {
              throw new Error(`IntelligenceXP(Read): failed to read file: ${path}`);
            }

            context.variables.lastOutput = content;
            addHistory(nodeId, `IntelligenceXP(Read): loaded ${content.length} chars`, { role: 'tool', lane: 'left' });
            break;
          }

          if (mode === 'index') {
            const action = String(node.data?.indexAction || 'start');
            const scopes =
              Array.isArray(node.data?.scopes) && node.data.scopes.length ? node.data.scopes.map(String) : ['documents'];

            if (!isRuVectorAvailable()) {
              addHistory(nodeId, 'IntelligenceXP(Index): desktop-only. No-op on web.', { role: 'system', lane: 'left' });
              break;
            }

            if (action === 'stop') {
              const res = await ruvectorIndexStop();
              if (!res.ok) throw new Error(res.error || 'IntelligenceXP(Index): stop failed.');
              addHistory(nodeId, 'IntelligenceXP(Index): stop requested.', { role: 'tool', lane: 'left' });
              break;
            }

            if (action === 'rebuild') {
              addHistory(nodeId, `IntelligenceXP(Index): rebuild • scopes=[${scopes.join(', ')}]`, { role: 'tool', lane: 'left' });
              const cleared = await ruvectorClear('os');
              if (!cleared.ok) throw new Error(cleared.error || 'IntelligenceXP(Index): clear failed.');
              const started = await ruvectorIndexStart(scopes);
              if (!started.ok) throw new Error(started.error || 'IntelligenceXP(Index): start failed.');
              addHistory(nodeId, 'IntelligenceXP(Index): rebuild started.', { role: 'tool', lane: 'left' });
              break;
            }

            // start
            addHistory(nodeId, `IntelligenceXP(Index): start • scopes=[${scopes.join(', ')}]`, { role: 'tool', lane: 'left' });
            const started = await ruvectorIndexStart(scopes);
            if (!started.ok) throw new Error(started.error || 'IntelligenceXP(Index): start failed.');
            addHistory(nodeId, 'IntelligenceXP(Index): indexing started.', { role: 'tool', lane: 'left' });
            break;
          }

          addHistory(nodeId, `IntelligenceXP: unknown mode "${mode}". Skipping.`, { role: 'system', lane: 'left' });
          break;
        }

        case 'agentic-flow': {
          const rawAgents = Array.isArray(node.data?.agents) ? node.data.agents : undefined;

          const agents = rawAgents
            ? rawAgents
                .map((a: any, idx: number) => ({
                  id: String(a?.id || `agent${idx + 1}`),
                  name: String(a?.name || `Agent ${idx + 1}`),
                  persona: String(
                    a?.persona ||
                      (idx === 0
                        ? 'You are Agent 1. Analyze and propose an initial position.'
                        : idx === 1
                          ? 'You are Agent 2. Challenge assumptions and ask clarifying questions.'
                          : `You are Agent ${idx + 1}. Add a fresh perspective and build on prior turns.`)
                  ),
                  modelId: a?.modelId ? String(a.modelId) : undefined,
                }))
                .slice(0, 10)
            : undefined;

          const config: AgenticFlowConfig = {
            agents,
            agent1Persona: String(node.data?.agent1Persona || 'You are Agent 1. Analyze and propose an initial position.'),
            agent2Persona: String(node.data?.agent2Persona || 'You are Agent 2. Challenge assumptions and ask clarifying questions.'),
            rounds: clamp(Number(node.data?.rounds ?? 3), 1, 60),
            modelSource: (node.data?.modelSource === 'custom' ? 'custom' : 'inherit') as 'inherit' | 'custom',
            inheritedModelId: String(context.variables.selectedModelId || ''),
            modelA: node.data?.modelA ? String(node.data.modelA) : undefined,
            modelB: node.data?.modelB ? String(node.data.modelB) : undefined,
            retrieval: {
              useLocal: Boolean(node.data?.retrieval?.useLocal ?? node.data?.useLocal ?? true),
              kbId: String(node.data?.retrieval?.kbId ?? node.data?.kbId ?? '') || undefined,
              topK: clamp(Number(node.data?.retrieval?.topK ?? node.data?.topK ?? 5), 1, 10),
              threshold: clamp(Number(node.data?.retrieval?.threshold ?? node.data?.threshold ?? 0.75), 0.6, 0.9),
              showSources: Boolean(node.data?.retrieval?.showSources ?? node.data?.showSources ?? true),
              autoIndex: Boolean(node.data?.retrieval?.autoIndex ?? node.data?.autoIndex ?? false),
            },
            returnMode: (['last', 'transcript', 'summary'] as const).includes(node.data?.returnMode)
              ? node.data.returnMode
              : 'last',
          };

          const rt = ensureRuntime(context);
          const initialLastSpeaker = agents && agents.length ? agents[agents.length - 1].id : 'agent2';

          rt.agentic![nodeId] = rt.agentic![nodeId] || {
            config,
            messages: [],
            // initialize so first speaker becomes Agent 1
            lastSpeaker: initialLastSpeaker,
          };
          rt.agentic![nodeId].config = config;

          addHistory(nodeId, `Agentic Flow: starting debate (${config.rounds} round(s))`, { role: 'system', lane: 'left' });

          const { lastModelUsed } = await runAgenticTurns({
            nodeId,
            context,
            rounds: config.rounds,
            appModels,
            addHistory: (msg, meta) => addHistory(nodeId, msg, meta),
            control,
          });

          const state = rt.agentic![nodeId];
          if (!state) break;

          const nameById = config.agents ? Object.fromEntries(config.agents.map((a) => [a.id, a.name])) : undefined;

          if (config.returnMode === 'transcript') {
            const out = transcriptToText(state.messages, nameById);
            context.variables.lastOutput = out;
            addHistory(nodeId, 'Agentic Flow: returning full transcript', { role: 'system', lane: 'left' });
          } else if (config.returnMode === 'summary') {
            const summary = await summarizeAgentic({
              context,
              nodeId,
              appModels,
              addHistory: (msg, meta) => addHistory(nodeId, msg, meta),
              modelHint: lastModelUsed,
            });
            context.variables.lastOutput = summary;
            addHistory(nodeId, summary || '(empty summary)', { role: 'tool', lane: 'left' });
          } else {
            addHistory(nodeId, 'Agentic Flow: returning last agent message', { role: 'system', lane: 'left' });
          }

          context.lastAgenticNodeId = nodeId;
          break;
        }

        case 'demythos': {
          const prompt = resolveMapleMythosPromptTemplate(String(node.data?.prompt || node.data?.content || '{{lastOutput}}'), context).trim();
          const modelId = String(node.data?.modelId || context.variables.selectedModelId || '');
          const selectedModel = appModels.find((candidate) => candidate.id === modelId) || appModels[0];

          if (!selectedModel) {
            throw new Error('DeMythos node could not find a usable model.');
          }

          addHistory(nodeId, `DeMythos: starting run with ${selectedModel.name}`, { role: 'system', lane: 'left' });

          const mapleMythosRun = await mapleMythosRuntime.startRun(
            {
              prompt,
              modelId: selectedModel.id,
              maxLoops: clamp(Number(node.data?.maxLoops ?? 6), 1, 50),
              reasoningMode: ['balanced', 'skeptical', 'expansive'].includes(node.data?.reasoningMode)
                ? node.data.reasoningMode
                : 'balanced',
              returnMode: ['final-answer', 'summary-answer', 'full-state'].includes(node.data?.returnMode)
                ? node.data.returnMode
                : 'full-state',
              stopOnConvergence: true,
              budget: {
                tokenBudget: node.data?.tokenBudget ? Number(node.data.tokenBudget) : undefined,
                creditBudget: node.data?.creditBudget ? Number(node.data.creditBudget) : undefined,
                timeBudgetMs: node.data?.timeBudgetMs ? Number(node.data.timeBudgetMs) : undefined,
              },
              origin: 'blueprint',
              title: `Blueprint DeMythos • ${node.data?.label || nodeId}`,
            },
            { appModels }
          );

          const mapleMythosOutput = await mapleMythosRuntime.waitForCompletion(mapleMythosRun.id);
          context.variables.demythos = mapleMythosOutput;
          context.variables[`${nodeId}_demythos`] = mapleMythosOutput;
          context.variables.demythosFinalAnswer = mapleMythosOutput.finalAnswer;
          context.variables.demythosTranscript = mapleMythosOutput.transcript;
          context.variables.demythosReasoningState = mapleMythosOutput.reasoningState;
          context.variables.demythosCreditsUsed = mapleMythosOutput.creditsUsed;
          context.variables.demythosTokensUsed = mapleMythosOutput.tokensUsed;
          context.variables.lastOutput =
            mapleMythosOutput.status === 'failed'
              ? mapleMythosOutput.finalAnswer || mapleMythosOutput.summary
              : node.data?.returnMode === 'final-answer'
                ? mapleMythosOutput.finalAnswer
                : node.data?.returnMode === 'summary-answer'
                  ? `${mapleMythosOutput.summary}\n\n${mapleMythosOutput.finalAnswer}`.trim()
                  : JSON.stringify(mapleMythosOutput, null, 2);

          addHistory(
            nodeId,
            `DeMythos: ${mapleMythosOutput.status} after ${mapleMythosOutput.loopCount} loop(s) • tokens ${mapleMythosOutput.tokensUsed} • credits ${mapleMythosOutput.creditsUsed > 0 && mapleMythosOutput.creditsUsed < 0.0001 ? '<0.0001' : mapleMythosOutput.creditsUsed.toFixed(4)}`,
            { role: 'tool', lane: 'left' }
          );

          if (mapleMythosOutput.status === 'failed') {
            throw new Error(mapleMythosOutput.summary || mapleMythosOutput.finalAnswer || 'DeMythos run failed.');
          }

          break;
        }

        case 'ai-response': {
          const messages: Partial<Message>[] = [];
          let combinedSystemContent = '';

          // Add content from System Prompt node
          if (context.variables.systemPrompt) {
            combinedSystemContent += context.variables.systemPrompt.trim();
          }

          // Add content from Knowledge Base node
          if (context.variables.knowledgeBaseResult) {
            if (combinedSystemContent) combinedSystemContent += '\n\n';
            combinedSystemContent += `Use the following context from the knowledge base to answer the user's query:\n\n${context.variables.knowledgeBaseResult}`;
          }

          // Add content from Data node (or any previous node's output) as additional context
          // Only add if lastOutput is different from userInput (to avoid duplicating the main question)
          if (context.variables.lastOutput && context.variables.lastOutput !== context.variables.userInput) {
            if (combinedSystemContent) combinedSystemContent += '\n\n';
            combinedSystemContent += `Here is additional data to consider:\n\n${context.variables.lastOutput}`;
          }

          if (combinedSystemContent.trim()) {
            messages.push({ role: 'system', content: combinedSystemContent.trim() });
          }

          // The user's explicit question from the run dialog
          messages.push({ role: 'user', content: context.variables.userInput });

          const targetModelId = context.variables.selectedModelId;
          const aiConfig = context.variables.aiConfig || {};
          let model;

          if (targetModelId) {
            model = appModels.find((m) => m.id === targetModelId);
            if (!model) {
              addHistory(nodeId, `Warning: Selected model ID "${targetModelId}" not found. Falling back to default.`, {
                role: 'system',
                lane: 'left',
              });
            }
          }

          if (!model) {
            // Fallback to original logic
            model = appModels.find((m) => m.provider === 'openai' && m.apiName.includes('gpt')) || appModels[0];
          }

          if (!model) throw new Error('No suitable AI model available for execution.');

          addHistory(nodeId, `Sending request to AI model "${model.name}"...`, { role: 'tool', lane: 'left' });
          const response = await sendMessageToModel(messages as Message[], model, appModels, aiConfig);

          context.variables.lastOutput = (response as any).content;
          addHistory(nodeId, `Received AI Response: "${(response as any).content}"`, { role: 'agent', lane: 'agent1' });
          break;
        }

        case 'end':
          context.finalOutput = context.variables.lastOutput || 'Flow completed without a final output.';
          addHistory(nodeId, `Executing End Node. Final Output: ${context.finalOutput}`, { role: 'system', lane: 'left' });
          break;

        default:
          addHistory(nodeId, `Skipping unknown node type: ${node.type}`, { role: 'system', lane: 'left' });
          break;
      }
    } catch (error: any) {
      const errorMessage = `Error at node ${node.data.label || node.id}: ${error.message}`;
      addHistory(nodeId, errorMessage, { role: 'system', lane: 'left' });

      // If the End node already captured a valid result, treat subsequent
      // errors as non-fatal warnings so a misconfigured downstream node
      // cannot overwrite an otherwise-successful run.
      if (context.finalOutput) {
        addHistory(nodeId, '(non-fatal: flow already completed via End node)', { role: 'system', lane: 'left' });
      } else {
        context.error = errorMessage;
        context.runStatus = 'failed';

        rt.visited = Array.from(visited);

        return context; // Stop execution on error
      }
    }

    // Cooperative stop/pause (checked at safe boundary after node execution).
    if (control?.shouldStop?.()) {
      addHistory(nodeId, 'Stopped by user.', { role: 'system', lane: 'left' });
      context.runStatus = 'stopped';
      rt.visited = Array.from(visited);
      return context;
    }

    if (control?.shouldPause?.()) {
      addHistory(nodeId, 'Paused by user.', { role: 'system', lane: 'left' });
      context.awaiting = {
        type: 'manual-pause',
        nodeId,
        reason: control?.pauseReason?.(),
        resumeMode: control?.pauseMode || 'blueprint',
      };
      context.runStatus = 'paused';
      rt.visited = Array.from(visited);
      return context;
    }

    // 5. Add next nodes in the sequence to the queue
    if (node.type !== 'conditional') {
      const neighbors = adj[nodeId] || [];
      nextNodeIds = neighbors.map((n) => n.target);
    }

    for (const neighborId of nextNodeIds) {
      if (!visited.has(neighborId)) {
        queue.push(neighborId);
      }
    }
  }

  rt.visited = Array.from(visited);

  if (!context.finalOutput && !context.error && !context.awaiting) {
    context.error = "The blueprint flow did not reach an 'End' node.";
    context.runStatus = 'failed';
  }

  if (!context.error && !context.awaiting) {
    context.runStatus = context.runStatus === 'stopped' ? 'stopped' : 'completed';
  }

  return context;
}

export async function executeBlueprint(
  nodes: Node[],
  edges: Edge[],
  initialInput: string,
  appModels: AIModel[],
  mcpServices: MCPService[],
  userApiKeys: Record<string, string>,
  options?: ExecuteBlueprintOptions
): Promise<ExecutionContext> {
  const context: ExecutionContext = {
    variables: { userInput: initialInput, lastOutput: initialInput },
    history: [
      {
        nodeId: 'start',
        message: `Start with input: "${initialInput}"`,
        timestamp: new Date().toISOString(),
        role: 'system',
        lane: 'left',
      },
    ],
    runtime: {
      visited: [],
      agentic: {},
    },
    runStatus: 'running',
  };

  // If Trust is disabled for this blueprint, inject skipTrust into aiConfig
  // so all sendMessageToModel calls bypass Trust prechecks/postchecks.
  if (options?.skipTrust) {
    context.variables.aiConfig = { ...context.variables.aiConfig, skipTrust: true };
  }

  // Emit initial context immediately (so UIs can mount in "live" mode).
  options?.onContext?.(context);

  return await runFromQueue({
    nodes,
    edges,
    context,
    queue: ['start'],
    visited: new Set<string>(),
    appModels,
    mcpServices,
    userApiKeys,
    onContext: options?.onContext,
    control: options?.control,
  });
}

export async function resumeBlueprint(
  nodes: Node[],
  edges: Edge[],
  priorContext: ExecutionContext,
  input: { nodeId: string; value: string },
  appModels: AIModel[],
  mcpServices: MCPService[],
  userApiKeys: Record<string, string>,
  options?: ExecuteBlueprintOptions
): Promise<ExecutionContext> {
  const awaiting = priorContext.awaiting;

  if (!awaiting) {
    return { ...priorContext, error: priorContext.error || 'Nothing to resume.' };
  }

  // Apply per-blueprint trust override on resume.
  if (options?.skipTrust) {
    priorContext.variables.aiConfig = { ...priorContext.variables.aiConfig, skipTrust: true };
  }

  // Manual pause: resume from the paused node without touching variables.
  if (awaiting.type === 'manual-pause') {
    priorContext.awaiting = undefined;
    priorContext.error = undefined;
    priorContext.runStatus = 'running';

    const next: string[] = edges.filter((e) => e.source === awaiting.nodeId).map((e) => e.target);

    const visited = new Set<string>(priorContext.runtime?.visited || []);
    visited.add(awaiting.nodeId);

    return await runFromQueue({
      nodes,
      edges,
      context: priorContext,
      queue: next,
      visited,
      appModels,
      mcpServices,
      userApiKeys,
      onContext: options?.onContext,
      control: options?.control,
    });
  }

  // Standard user-decision resume.
  if (awaiting.type !== 'user-decision') {
    return { ...priorContext, error: priorContext.error || 'Nothing to resume.' };
  }

  if (awaiting.nodeId !== input.nodeId) {
    return { ...priorContext, error: `Cannot resume: expected node ${awaiting.nodeId} but got ${input.nodeId}.` };
  }

  const v = String(input.value ?? '');
  if (!awaiting.allowEmpty && !v.trim()) {
    return { ...priorContext, error: 'Input is required to continue.' };
  }

  // Write to variables and clear awaiting state
  priorContext.variables[awaiting.variable] = v;
  priorContext.variables.lastOutput = v;
  priorContext.awaiting = undefined;
  priorContext.error = undefined;
  priorContext.runStatus = 'running';

  // Build adjacency to find neighbors of the decision node
  const next: string[] = edges.filter((e) => e.source === input.nodeId).map((e) => e.target);

  const visited = new Set<string>(priorContext.runtime?.visited || []);
  visited.add(input.nodeId);

  return await runFromQueue({
    nodes,
    edges,
    context: priorContext,
    queue: next,
    visited,
    appModels,
    mcpServices,
    userApiKeys,
    onContext: options?.onContext,
    control: options?.control,
  });
}

export async function continueBlueprintFrom(
  nodes: Node[],
  edges: Edge[],
  priorContext: ExecutionContext,
  startNodeIds: string[],
  appModels: AIModel[],
  mcpServices: MCPService[],
  userApiKeys: Record<string, string>,
  options?: ExecuteBlueprintOptions
): Promise<ExecutionContext> {
  const visited = new Set<string>(priorContext.runtime?.visited || []);
  const queue = (startNodeIds || []).map(String).filter(Boolean);
  priorContext.error = undefined;
  priorContext.runStatus = 'running';

  return await runFromQueue({
    nodes,
    edges,
    context: priorContext,
    queue,
    visited,
    appModels,
    mcpServices,
    userApiKeys,
    onContext: options?.onContext,
    control: options?.control,
  });
}

export async function continueAgenticFlow(
  nodeId: string,
  priorContext: ExecutionContext,
  userMessage: string,
  rounds: number | undefined,
  appModels: AIModel[]
): Promise<ExecutionContext> {
  const rt = ensureRuntime(priorContext);
  const state = rt.agentic?.[nodeId];
  if (!state) {
    return { ...priorContext, error: 'Agentic Flow state not found for follow-up.' };
  }

  const msg = String(userMessage || '').trim();
  if (!msg) {
    return priorContext;
  }

  state.messages.push({ speaker: 'user', content: msg });
  priorContext.variables.lastOutput = msg;

  await runAgenticTurns({
    nodeId,
    context: priorContext,
    rounds: clamp(Number(rounds ?? 1), 1, 60),
    appModels,
    addHistory: (m, meta) => {
      priorContext.history.push({
        nodeId,
        message: m,
        timestamp: new Date().toISOString(),
        ...meta,
      });
    },
  });

  // For follow-ups, always surface the most recent agent message
  const lastAgent = [...state.messages].reverse().find((m) => m.speaker !== 'user');
  if (lastAgent) {
    priorContext.finalOutput = lastAgent.content;
    priorContext.variables.lastOutput = lastAgent.content;
  }

  priorContext.lastAgenticNodeId = nodeId;

  return priorContext;
}